/**
 * Thin fetch wrapper for the ÚKLID-KSENA API.
 *
 * - Reads token from `useAuthStore.getState().token`
 * - Auto-attaches `Authorization: Bearer` header
 * - Handles 401 → refresh token flow (one retry)
 * - Base URL from `VITE_API_URL` env or "" (same-origin via Vite proxy / Caddy)
 */

import type { ApiError, User } from "@/types";
import { assertCurrentSession, getSession, SessionChangedError } from "./sessionLifecycle";
import {
  ensureTelemetrySession,
  getActiveWorkflowAttemptId,
  getClientVersion,
} from "@/utils/telemetryContext";
import { CLIENT_ID } from "./clientId";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * We lazily import the auth store to avoid circular imports
 * (api.ts ← authStore.ts ← api.ts). The store module is only
 * resolved at call-time, not on module load.
 */
let _getAuthState: (() => {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  refreshAuth: () => Promise<boolean>;
  logout: () => void;
}) | null = null;

async function getAuthState() {
  if (!_getAuthState) {
    const mod = await import("@/stores/authStore");
    _getAuthState = () => mod.useAuthStore.getState();
  }
  return _getAuthState();
}

async function refreshAccessToken(session: ReturnType<typeof getSession>, failedToken: string | null): Promise<string | null> {
  const auth = await getAuthState();
  assertCurrentSession(session);
  // A concurrent request may already have refreshed this access token.
  if (auth.token && auth.token !== failedToken) return auth.token;
  const refreshed = await auth.refreshAuth();
  assertCurrentSession(session);
  return refreshed ? (await getAuthState()).token : null;
}

class ApiRequestError extends Error {
  status: number;
  code: string;
  requestId: string | null;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown, code?: string, requestId?: string | null) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code ?? `HTTP_${status}`;
    this.requestId = requestId ?? null;
    this.details = details;
  }
}

export { ApiRequestError };

// ---------------------------------------------------------------------------
// Core request function
// ---------------------------------------------------------------------------

async function request<T>(
  url: string,
  options: RequestInit = {},
  _isRetry = false,
  session = getSession(),
): Promise<T> {
  const auth = await getAuthState();
  assertCurrentSession(session);
  const requestId = crypto.randomUUID();
  const requestStartedAt = performance.now();

  const headers = new Headers(options.headers);
  // Don't force JSON content-type when the body is FormData — the browser
  // sets the multipart boundary automatically.
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  if (!isFormData && !headers.has("Content-Type") && options.body) {
    headers.set("Content-Type", "application/json");
  }
  if (auth.token) {
    headers.set("Authorization", `Bearer ${auth.token}`);
  }
  headers.set("X-Request-ID", requestId);
  headers.set("X-Client-Version", getClientVersion());
  headers.set("X-Client-Id", CLIENT_ID);
  if (auth.user?.id) {
    headers.set("X-Telemetry-Session-ID", ensureTelemetrySession(auth.user.id).sessionId);
  }
  const workflowAttemptId = getActiveWorkflowAttemptId();
  if (workflowAttemptId) headers.set("X-Workflow-Attempt-ID", workflowAttemptId);

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${url}`, {
      ...options,
      headers,
      credentials: "include", // send httpOnly cookies too
      signal: session.controller.signal,
    });
  } catch (error) {
    if (getSession() !== session) throw new SessionChangedError();
    window.dispatchEvent(new CustomEvent("uklid:api-network-error", {
      detail: {
        requestId,
        operation: `${options.method ?? "GET"} ${new URL(url, window.location.origin).pathname}`,
        durationMs: Math.round(performance.now() - requestStartedAt),
      },
    }));
    throw error;
  }
  assertCurrentSession(session);

  // 204 No Content
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  // Try to parse JSON
  let data: unknown;
  const contentType = response.headers.get("Content-Type");
  if (contentType?.includes("application/json")) {
    data = await response.json();
  } else {
    data = await response.text();
  }
  assertCurrentSession(session);

  // Success
  if (response.ok) {
    return data as T;
  }

  // 401 — attempt a single token refresh, then retry
  // Refresh works via httpOnly cookie even if refreshToken isn't stored in memory
  if (response.status === 401 && !_isRetry) {
    const accessToken = await refreshAccessToken(session, auth.token);
    if (accessToken) {
      return request<T>(url, options, true, session);
    }
  }

  // Error response
  const apiError = data as ApiError;
  throw new ApiRequestError(
    apiError?.error ?? `Request failed (${response.status})`,
    response.status,
    apiError?.details,
    apiError?.code ?? response.headers.get("x-error-code") ?? undefined,
    response.headers.get("x-request-id") ?? requestId,
  );
}

// ---------------------------------------------------------------------------
// Public methods
// ---------------------------------------------------------------------------

export function get<T>(url: string): Promise<T> {
  return request<T>(url, { method: "GET" });
}

/** `keepalive` lets small beacons sent while the page is being hidden finish. */
export function post<T>(url: string, body?: unknown, init: Pick<RequestInit, "keepalive"> = {}): Promise<T> {
  return request<T>(url, {
    ...init,
    method: "POST",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

/** Multipart POST. Caller passes a FormData; we let the browser set the boundary. */
export function postForm<T>(url: string, formData: FormData): Promise<T> {
  return request<T>(url, {
    method: "POST",
    body: formData,
    // Explicitly do NOT set Content-Type so the browser adds the multipart boundary.
    headers: {},
  });
}

/** Multipart PUT. Caller passes a FormData; we let the browser set the boundary. */
export function putForm<T>(url: string, formData: FormData): Promise<T> {
  return request<T>(url, {
    method: "PUT",
    body: formData,
    headers: {},
  });
}

export function put<T>(url: string, body?: unknown): Promise<T> {
  return request<T>(url, {
    method: "PUT",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function patch<T>(url: string, body?: unknown): Promise<T> {
  return request<T>(url, {
    method: "PATCH",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function del<T = void>(url: string): Promise<T> {
  return request<T>(url, { method: "DELETE" });
}

/**
 * GET that returns a Blob. Used for binary downloads (PDFs, images) where
 * we still want the bearer-token auth + refresh flow that `request` provides.
 * Bypasses request() to avoid its JSON/text parsing.
 */
function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      return encoded[1];
    }
  }
  return /filename="([^"]+)"/i.exec(header)?.[1] ?? null;
}

export async function getBlobDownload(url: string): Promise<{ blob: Blob; filename: string | null }> {
  const session = getSession();
  const auth = await getAuthState();
  assertCurrentSession(session);
  const fetchWithAuth = async (token: string | null): Promise<Response> => {
    const headers = new Headers();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return fetch(`${BASE_URL}${url}`, {
      method: "GET",
      headers,
      credentials: "include",
      signal: session.controller.signal,
    });
  };

  let response = await fetchWithAuth(auth.token);
  assertCurrentSession(session);

  if (response.status === 401) {
    const accessToken = await refreshAccessToken(session, auth.token);
    if (accessToken) {
      response = await fetchWithAuth(accessToken);
    }
  }
  assertCurrentSession(session);

  if (!response.ok) {
    const contentType = response.headers.get("Content-Type");
    if (contentType?.includes("application/json")) {
      const data = await response.json() as ApiError;
      throw new ApiRequestError(
        data?.error ?? `Request failed (${response.status})`,
        response.status,
        data?.details,
        data?.code ?? response.headers.get("x-error-code") ?? undefined,
        response.headers.get("x-request-id"),
      );
    }
    throw new ApiRequestError(
      `Request failed (${response.status})`,
      response.status,
      undefined,
      response.headers.get("x-error-code") ?? undefined,
      response.headers.get("x-request-id"),
    );
  }
  const blob = await response.blob();
  assertCurrentSession(session);
  return {
    blob,
    filename: filenameFromContentDisposition(response.headers.get("Content-Disposition")),
  };
}

/**
 * JSON POST whose successful response body is read incrementally by the caller
 * (e.g. server-sent events). Shares the bearer-token auth + single refresh retry.
 */
export async function postStream(url: string, body: unknown, signal?: AbortSignal): Promise<Response> {
  const session = getSession();
  const auth = await getAuthState();
  assertCurrentSession(session);
  const controller = new AbortController();
  for (const source of [session.controller.signal, signal]) {
    if (source?.aborted) controller.abort();
    source?.addEventListener("abort", () => controller.abort(), { once: true });
  }
  const send = (token: string | null): Promise<Response> => {
    const headers = new Headers({ "Content-Type": "application/json", Accept: "text/event-stream" });
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return fetch(`${BASE_URL}${url}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      credentials: "include",
      signal: controller.signal,
    });
  };

  let response = await send(auth.token);
  assertCurrentSession(session);
  if (response.status === 401) {
    const accessToken = await refreshAccessToken(session, auth.token);
    if (accessToken) response = await send(accessToken);
  }
  assertCurrentSession(session);
  if (response.ok) return response;

  const data = await response.json().catch(() => null) as ApiError | null;
  throw new ApiRequestError(
    data?.error ?? `Request failed (${response.status})`,
    response.status,
    data?.details,
    data?.code ?? response.headers.get("x-error-code") ?? undefined,
    response.headers.get("x-request-id"),
  );
}

export async function getBlob(url: string): Promise<Blob> {
  return (await getBlobDownload(url)).blob;
}

const api = { get, post, postForm, put, putForm, patch, del, getBlob, getBlobDownload, postStream };
export default api;
